import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  t,
  throttle,
  isDesktop,
  getPriceSavingsInformation,
  isLargeTablet,
  getVatPriceProps,
} from '@insight/toolkit-utils';
import { BetterTogetherNewListItem } from './BetterTogetherNewListItem';
import { Button, Currency, PlusIcon, CalloutBox } from '../../index';

export const getWebPricingFeatureFlag = () =>
  window.flags && window.flags['GNA-11926-NEW-WEB-PRICING'];

export const BetterTogetherNewList = ({
  betterTogetherList,
  selectedProducts,
  materialId,
  addToCartState,
  removeProduct,
  addProduct,
  addItemsToCart,
  currency,
  isLoggedIn,
  showVAT,
  setMiniPDP,
  webGroupPermissions,
  showPrice,
  loginLink,
  accountLink,
}) => {
  const newWebPriceFeature = getWebPricingFeatureFlag();
  const { enableListPrice, disableStockAndPrice: isStockAndPriceDisabled } =
    webGroupPermissions;
  const [isOnMobile, setIsMobile] = useState(!isLargeTablet() && !isDesktop());
  useEffect(() => {
    const onResize = throttle(() => {
      setIsMobile(!isLargeTablet() && !isDesktop());
    }, 250);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Convert the selected products to a list
  const selectedProductsList = Object.values(selectedProducts);
  // Get the total price of the selected products
  const totalPriceRaw = selectedProductsList.reduce(
    (acc, product) => acc + Number(product?.price?.price || 0),
    0
  );

  const instantSavingsTotal =
    newWebPriceFeature || isLoggedIn
      ? selectedProductsList.reduce((acc, product) => {
          const nListPrice = Number(product.price?.listPrice || 0);
          const nInsightPrice = Number(product.price?.price || 0);
          const { priceSavings, showListPriceAndSavings } =
            getPriceSavingsInformation(nListPrice, nInsightPrice);
          return acc + (showListPriceAndSavings ? priceSavings : 0);
        }, 0)
      : 0;

  const cartTotal = Object.keys(selectedProducts).length;
  const isDisabled = cartTotal === 0;

  const renderTotalPrice = () => {
    if (isDisabled || isStockAndPriceDisabled) return null;
    const { exclVatProps } = getVatPriceProps(showVAT);
    return (
      <div className="c-product-better-together__total-price">
        {!!instantSavingsTotal &&
          instantSavingsTotal > 0 &&
          enableListPrice && (
            <div className="c-product-better-together__savings o-grid o-grid--justify-between">
              <span className="o-grid__item c-product-better-together__savings__label">
                {`${t('Instant savings')}:`}
              </span>
              <span className="o-grid__item u-text-right">
                <Currency currencyCode={currency} value={instantSavingsTotal} />
              </span>
            </div>
          )}
        <div className="c-product-better-together__total o-grid o-grid--justify-between">
          <span className="c-product-better-together__total-price__label o-grid__item ">
            {t('Total price:')}
          </span>
          <span className="o-grid__item u-text-right">
            <Currency
              currencyCode={currency}
              value={totalPriceRaw}
              {...exclVatProps}
              size="large"
            />
          </span>
        </div>
      </div>
    );
  };
  const renderProductList = () => {
    if (!betterTogetherList?.length) return null;
    return (
      <>
        {betterTogetherList.map((currentProduct, index) => {
          const isLast = index + 1 === betterTogetherList.length;
          const showMobilePlus = index % 2 === 0;
          const isCurrentItem = currentProduct.materialId === materialId;
          const isSelected = Boolean(
            selectedProducts[currentProduct.materialId]
          );
          return (
            <Fragment key={`${index}_${currentProduct.materialId}`}>
              <BetterTogetherNewListItem
                {...currentProduct}
                isCurrentItem={isCurrentItem}
                isSelected={isSelected}
                isDisabled={addToCartState.loading}
                onSelect={() =>
                  isSelected
                    ? removeProduct(currentProduct)
                    : addProduct(currentProduct)
                }
                isLoggedIn={isLoggedIn}
                showVAT={showVAT}
                setMiniPDP={setMiniPDP}
                webGroupPermissions={webGroupPermissions}
                newWebPriceFeature={newWebPriceFeature}
                showPrice={showPrice}
              />
              {!isLast ? (
                <div
                  className={cn(
                    { 'u-hide': isOnMobile && showMobilePlus },
                    'c-product-better-together__plus-sign__container o-grid__item o-grid__item--shrink'
                  )}
                >
                  <PlusIcon className="c-product-better-together__plus-sign" />
                </div>
              ) : null}
            </Fragment>
          );
        })}
        <div
          className={cn(
            {
              'c-product-better-together__CTA__no-Padding':
                betterTogetherList.length === 3,
            },
            'c-product-better-together__CTA o-grid__item'
          )}
        >
          <div className="c-product-better-together__CTA-container">
            {showPrice ? (
              <>
                {renderTotalPrice()}
                {isDisabled ? (
                  <Button
                    className="c-product-better-together__add-to-cart c-button--block"
                    color="primary"
                    size="small"
                    disabled
                  >
                    {t('No items selected')}
                  </Button>
                ) : (
                  <Button
                    className={cn(
                      'c-product-better-together__add-to-cart c-button--block',
                      {
                        'c-product-better-together__add-to-cart-margin':
                          !isStockAndPriceDisabled,
                      }
                    )}
                    color="primary"
                    size="small"
                    disabled={addToCartState.loading}
                    isLoading={addToCartState.loading}
                    onClick={addItemsToCart}
                  >
                    {`${t('Add')} ${cartTotal} ${cartTotal === 1 ? t('item to cart') : t('items to cart')}`}
                  </Button>
                )}
              </>
            ) : (
              <CalloutBox loginLink={loginLink} accountLink={accountLink} />
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div>
      <div className="u-hide@tablet u-text-bold">{`${cartTotal} ${cartTotal === 1 ? t('item selected') : t('items selected')}`}</div>
      <div className="c-product-better-together__images">
        <div className="o-grid o-grid--gutters-small">
          {renderProductList()}
        </div>
      </div>
    </div>
  );
};

BetterTogetherNewList.propTypes = {
  betterTogetherList: PropTypes.arrayOf(
    PropTypes.shape({
      materialId: PropTypes.string.isRequired,
      imageUrl: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.shape({
        price: PropTypes.number.isRequired,
      }),
    })
  ),
  selectedProducts: PropTypes.objectOf(
    PropTypes.shape({
      materialId: PropTypes.string.isRequired,
      imageUrl: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.shape({
        price: PropTypes.number.isRequired,
      }),
    })
  ),
  materialId: PropTypes.string,
  addToCartState: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
  }),
  removeProduct: PropTypes.func.isRequired,
  addProduct: PropTypes.func.isRequired,
  addItemsToCart: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  showPrice: PropTypes.bool,
  loginLink: PropTypes.string,
  accountLink: PropTypes.string,
};

BetterTogetherNewList.defaultProps = {
  showPrice: true,
};
